import { FC } from 'react'
import { SentimentDonut } from 'src/components/SentimentDonut'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { ReviewComparisonSentiment } from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './sentimentDistribution.module.scss'

interface SentimentDistributionProps {
  splits: ReviewComparisonSentiment[]
}

const COPY = {
  reviews: 'Reviews',
  reviewsLabel: 'Reviews:',
  subtitle: 'Share of positive, neutral and negative reviews',
  title: 'Sentiment Distribution',
}

// The feature's sentiment palette, used everywhere in Review Analysis.
const BANDS: {
  color: string
  key: 'negative' | 'neutral' | 'positive'
  label: string
}[] = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#c7ced1', key: 'neutral', label: 'Neutral' },
  { color: '#ff6833', key: 'negative', label: 'Negative' },
]

const DONUT_SIZE = 190

const formatCount = (value: number) => value.toLocaleString('en-US')

const card = (split: ReviewComparisonSentiment) => {
  const bands = BANDS.map((band) => ({
    ...band,
    value: split[band.key],
  }))
  const total = bands.reduce((sum, band) => sum + band.value, 0)
  // A facility with no reviews would divide by zero, and a card of NaN% is
  // worse than a card of zeroes.
  const share = (value: number) =>
    total ? Math.round((value / total) * 100) : 0

  return (
    <article className={styles.card} key={split.facilityId}>
      <h4 className={styles.facility}>
        <TruncatedTooltip text={split.facilityName} />
      </h4>

      <div className={styles.body}>
        <div className={styles.breakdown}>
          <p className={styles.total}>
            {COPY.reviewsLabel}{' '}
            <b className={styles.totalValue}>{formatCount(total)}</b>
          </p>

          <ul className={styles.bands}>
            {bands.map((band) => (
              <li className={styles.band} key={band.label}>
                <span
                  className={styles.bandDot}
                  style={{ background: band.color }}
                />
                {/* Share then band, as one phrase: "72% Positive" is what the
                    row says out loud, and splitting it into columns made the
                    reader assemble it. */}
                <span className={styles.bandLabel}>
                  {share(band.value)}% {band.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.donut}>
          {/* Legend order, so the ring reads in the order the rows beside it
              are listed: positive from twelve o'clock, then neutral, then
              negative closing the circle. */}
          <SentimentDonut
            bands={bands.map((band) => ({
              color: band.color,
              value: band.value,
            }))}
            size={DONUT_SIZE}
          />
          <span className={styles.donutCenter}>
            <span className={styles.donutCaption}>{COPY.reviews}</span>
            <span className={styles.donutTotal}>{formatCount(total)}</span>
          </span>
        </div>
      </div>
    </article>
  )
}

export const SentimentDistribution: FC<SentimentDistributionProps> = ({
  splits,
}) => (
  <section>
    <div className={styles.header}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.cards}>{splits.map(card)}</div>
  </section>
)
